import { ButtonOutlined, ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import PoweredByKodiak from 'components/PoweredByKodiak'
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react'
import { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'

const Wrapper = styled.div`
  width: 840px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
     width: 560px;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
     width: 100%;
  `};
`

const BackButton = styled.div`
  display: flex;
  padding: 8px 0px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  align-self: flex-start;

  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  cursor: pointer;

  transition: all 0.3s ease-in-out;
  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 700;
  }
`

const IslandCardFlex = styled.div`
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  border-radius: 8px;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.09);
  padding: 24px;

  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  gap: 24px;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.accentFailure}15;
  margin-bottom: 8px;
`

const FlexRowCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`

const PoolIdContainer = styled.div`
  background: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  border-radius: 8px;
  padding: 12px 16px;
  margin: 16px auto;
  max-width: 400px;
`

interface PoolNotFoundProps {
  error?: any
  poolId?: string
  onRetry?: () => void
  onBackToPoolsList?: () => void
}

function PoolNotFoundUI({ error, poolId, onRetry, onBackToPoolsList }: PoolNotFoundProps) {
  const theme = useContext(ThemeContext)

  const getErrorDetails = () => {
    const errorMessage = error?.message?.toLowerCase() || ''

    if (errorMessage.includes('not found') || errorMessage.includes('404')) {
      return {
        title: 'Pool Not Found',
        message: "This pool ID doesn't exist. Please verify the pool address and try again.",
        canRetry: true,
      }
    }

    if (errorMessage.includes('invalid address') || errorMessage.includes('invalid pool address')) {
      return {
        title: 'Invalid Pool Address',
        message:
          'The pool address format is invalid. Please check that you have entered a valid Ethereum address (42 characters starting with 0x).',
        canRetry: false,
      }
    }

    if (errorMessage.includes('network') || errorMessage.includes('connection') || errorMessage.includes('fetch')) {
      return {
        title: 'Connection Error',
        message: 'Unable to connect to the network. Please check your connection and try again.',
        canRetry: true,
      }
    }

    if (errorMessage.includes('unsupported') || errorMessage.includes('chain')) {
      return {
        title: 'Unsupported Network',
        message: 'This pool is not available on your current network. Please switch to a supported network.',
        canRetry: false,
      }
    }

    return {
      title: 'Pool Not Supported',
      message: 'This pool type is not currently supported. We support Island, V2, and Charm pools.',
      canRetry: true,
    }
  }

  const { title, message, canRetry } = getErrorDetails()

  return (
    <>
      <Wrapper>
        <BackButton onClick={onBackToPoolsList}>
          <ArrowLeft size={20} />
          Pools
        </BackButton>

        <IslandCardFlex>
          <ErrorContainer>
            <IconContainer>
              <AlertCircle size={28} color={theme.accentFailure} />
            </IconContainer>

            <AutoColumn gap="sm" style={{ alignItems: 'center' }}>
              <ThemedText.DeprecatedBody fontSize="24px" fontWeight="600" lineHeight="32px" color={theme.textPrimary}>
                {title}
              </ThemedText.DeprecatedBody>

              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="400"
                lineHeight="24px"
                color={theme.textSecondary}
                style={{ maxWidth: '480px' }}
              >
                {message}
              </ThemedText.DeprecatedBody>

              {poolId && (
                <PoolIdContainer>
                  <ThemedText.DeprecatedBody
                    fontSize="12px"
                    fontWeight="400"
                    lineHeight="16px"
                    color={theme.textTertiary}
                  >
                    Pool ID
                  </ThemedText.DeprecatedBody>
                  <ThemedText.DeprecatedBody
                    fontSize="14px"
                    fontWeight="500"
                    lineHeight="20px"
                    color={theme.textSecondary}
                    style={{
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      marginTop: '4px',
                    }}
                  >
                    {poolId}
                  </ThemedText.DeprecatedBody>
                </PoolIdContainer>
              )}
            </AutoColumn>

            <FlexRowCenter>
              <ButtonPrimary onClick={onBackToPoolsList} style={{ minWidth: '140px' }}>
                {/* <Search size={16} style={{ marginRight: '8px' }} /> */}
                Browse Pools
              </ButtonPrimary>

              {canRetry && onRetry && (
                <ButtonOutlined onClick={onRetry} style={{ minWidth: '120px' }}>
                  <RefreshCw size={16} style={{ marginRight: '8px' }} />
                  Try Again
                </ButtonOutlined>
              )}
            </FlexRowCenter>
          </ErrorContainer>
        </IslandCardFlex>
      </Wrapper>

      <PoweredByKodiak />
    </>
  )
}

export default PoolNotFoundUI
